import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MailOpen } from 'lucide-react'
import { getEmails, getMailboxes, markEmailAsRead } from '../api/mail'
import { EmptyState } from '../components/EmptyState'
import { SearchInput } from '../components/SearchInput'
import { Modal } from '../components/Modal'
import { Select, SelectItem } from '../components/ui/select'
import { useDateFmt } from '../hooks/useDateFmt'
import type { Email, Mailbox } from '../types'

const ALL_MAILBOXES = '_all'

export function Emails() {
  const fmt = useDateFmt()
  const [mailboxId, setMailboxId] = useState('')
  const [selected, setSelected] = useState<Email | null>(null)
  const queryClient = useQueryClient()

  const { data: mailboxes = [] } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: () => getMailboxes().then((r) => r.data),
  })

  const { data: emails = [], isLoading } = useQuery({
    queryKey: ['emails', mailboxId],
    queryFn: () => getEmails({ mailboxId: mailboxId || undefined, limit: 200 }).then((r) => r.data),
  })

  const readMutation = useMutation({
    mutationFn: (id: string) => markEmailAsRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['emails'] }),
  })

  const openEmail = (e: Email) => {
    setSelected(e)
    if (!e.isRead) readMutation.mutate(e.id)
  }

  const [search, setSearch] = useState('')
  const filtered = emails.filter((e: Email) =>
    [e.subject ?? '', e.sender, e.recipient].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Emails</h1>
          <p className="text-sm text-ink-dim mt-0.5">Emails captured by monitored mailboxes</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search emails" />
          <Select
            value={mailboxId || ALL_MAILBOXES}
            onValueChange={(v) => setMailboxId(v === ALL_MAILBOXES ? '' : v)}
            className="w-44"
          >
            <SelectItem value={ALL_MAILBOXES}>All mailboxes</SelectItem>
            {mailboxes.map((mb: Mailbox) => <SelectItem key={mb.id} value={mb.id}>{mb.name}</SelectItem>)}
          </Select>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : emails.length === 0 ? (
          <EmptyState icon={MailOpen} title="No emails" description="Emails appear here as mailboxes receive them" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Subject</th>
                <th>From</th>
                <th>To</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e: Email) => (
                <tr
                  key={e.id}
                  onClick={() => openEmail(e)}
                  className="cursor-pointer"
                >
                  <td>
                    {!e.isRead && (
                      <span className="inline-block w-2 h-2 rounded-full bg-accent" title="Unread" />
                    )}
                  </td>
                  <td className={e.isRead ? 'text-ink-dim' : 'font-semibold text-ink'}>
                    {e.subject || '(no subject)'}
                  </td>
                  <td className="text-ink-dim truncate max-w-xs">{e.sender}</td>
                  <td className="text-ink-dim text-xs truncate max-w-xs">{e.recipient || ''}</td>
                  <td className="text-ink-dim">{fmt(e.receivedTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Email detail panel */}
      {selected && (
        <Modal title={selected.subject || '(no subject)'} onClose={() => setSelected(null)} size="lg">
          <div className="space-y-1 -mt-2 mb-4">
            <p className="text-xs text-ink-dim">From: {selected.sender} · To: {selected.recipient || ''}</p>
            <p className="text-xs text-ink-dim">{fmt(selected.receivedTime)}</p>
          </div>
          <div className="text-sm text-ink whitespace-pre-wrap">
            {selected.body || <span className="text-ink-dim italic">No body</span>}
          </div>
        </Modal>
      )}
    </div>
  )
}
